import { useCallback, useEffect, useMemo, useState } from "react";
import { usePoll } from "@/hooks/usePoll";
import { Link } from "react-router-dom";
import { crewLabel } from "@/lib/permissions";
import {
  CaretLeft,
  CaretRight,
  User,
  Ruler,
  Wrench,
  Truck,
  Lifebuoy,
  MapPin,
  Phone,
  Clock,
  ArrowRight,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { AppHeader } from "@/components/AppHeader";
import { api } from "@/lib/api";

const DOW = ["S", "M", "T", "W", "T", "F", "S"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const KIND_DOT = {
  Template: "bg-purple-400",
  Fabrication: "bg-yellow-400",
  Install: "bg-blue-400",
  Service: "bg-red-400",
};
const DOT_HEX = {
  Template: "#c084fc",
  Fabrication: "#facc15",
  Install: "#60a5fa",
  Service: "#f87171",
};
const KIND_STYLE = {
  Template: "border-purple-500/30 bg-purple-500/10 text-purple-300",
  Fabrication: "border-yellow-500/30 bg-yellow-500/10 text-yellow-300",
  Install: "border-blue-500/30 bg-blue-500/10 text-blue-300",
  Service: "border-red-500/30 bg-red-500/10 text-red-300",
};
const KIND_BAR = {
  Template: "bg-purple-400",
  Fabrication: "bg-yellow-400",
  Install: "bg-blue-400",
  Service: "bg-red-400",
};
const KIND_ICON = {
  Template: Ruler,
  Fabrication: Wrench,
  Install: Truck,
  Service: Lifebuoy,
};

const STATUS_STYLE = {
  Done: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300",
  "On site": "border-blue-500/40 bg-blue-500/15 text-blue-300",
  "En route": "border-amber-500/40 bg-amber-500/15 text-amber-300",
  Loaded: "border-amber-500/40 bg-amber-500/15 text-amber-300",
  Started: "border-amber-500/40 bg-amber-500/15 text-amber-300",
  Scheduled: "border-zinc-700 bg-zinc-800 text-zinc-300",
};
const stopStat = (e) => (e.stop_status && e.stop_status.status) || "";
const statusLabel = (e) => stopStat(e) || "Scheduled";
const isDone = (e) => stopStat(e) === "Done";

const iso = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

export default function JobSchedule() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [events, setEvents] = useState([]);
  const [selected, setSelected] = useState(
    iso(today.getFullYear(), today.getMonth(), today.getDate()),
  );
  const [hideDone, setHideDone] = useState(false);

  const load = useCallback(() => {
    api
      .get("/jobs/schedule", {
        params: { month: `${year}-${String(month + 1).padStart(2, "0")}` },
      })
      .then(({ data }) => setEvents(data.events))
      .catch(() => {});
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);
  usePoll(load);

  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      map[e.date] = map[e.date] || [];
      map[e.date].push(e);
    });
    return map;
  }, [events]);

  const firstDow = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const cells = [
    ...Array(firstDow).fill(null),
    ...Array.from({ length: days }, (_, i) => i + 1),
  ];
  const shift = (delta) => {
    const d = new Date(year, month + delta, 1);
    setYear(d.getFullYear());
    setMonth(d.getMonth());
  };
  const dayEvents = byDate[selected] || [];
  const doneCount = dayEvents.filter(isDone).length;
  const visibleDay = hideDone ? dayEvents.filter((e) => !isDone(e)) : dayEvents;

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Jobs / Schedule" />
      <main className="relative z-10 pb-20 pt-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-600">
              Jobs / Schedule
            </div>
            <h1
              className="text-2xl font-bold tracking-tight sm:text-3xl"
              data-testid="schedule-title"
            >
              {MONTHS[month]} {year}
            </h1>
            <p
              className="mt-1 text-sm text-zinc-500"
              data-testid="schedule-count"
            >
              {events.length} template, fab, install and service slots this
              month
            </p>
          </div>
          <div className="flex gap-2">
            <Link
              to="/projects/team-load"
              data-testid="schedule-team-load-link"
              className="flex h-11 items-center rounded-md border border-zinc-700 px-4 text-xs font-semibold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              Team Load
            </Link>
            <Link
              to="/projects"
              data-testid="schedule-board-link"
              className="flex h-11 items-center rounded-md border border-zinc-700 px-4 text-xs font-semibold uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              Stage Board
            </Link>
            <Button
              data-testid="schedule-prev"
              variant="outline"
              size="icon"
              onClick={() => shift(-1)}
              className="h-11 w-11 border-zinc-700 bg-transparent hover:bg-zinc-800"
            >
              <CaretLeft size={18} weight="bold" />
            </Button>
            <Button
              data-testid="schedule-next"
              variant="outline"
              size="icon"
              onClick={() => shift(1)}
              className="h-11 w-11 border-zinc-700 bg-transparent hover:bg-zinc-800"
            >
              <CaretRight size={18} weight="bold" />
            </Button>
          </div>
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-[2fr_1fr]">
          <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-3">
            <div className="grid grid-cols-7 text-center text-xs uppercase tracking-widest text-zinc-600">
              {DOW.map((d, i) => (
                <div key={`${d}-${i}`} className="py-2">
                  {d}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                if (!d) return <div key={`e${i}`} />;
                const key = iso(year, month, d);
                const list = byDate[key] || [];
                return (
                  <button
                    key={key}
                    data-testid={`schedule-day-${key}`}
                    onClick={() => setSelected(key)}
                    className={`flex min-h-[76px] flex-col items-start rounded-md border p-2 text-left transition-all duration-150 ${
                      key === selected
                        ? "border-indigo-400/60 bg-indigo-500/10"
                        : list.length
                          ? "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
                          : "border-zinc-900 hover:border-zinc-800"
                    }`}
                  >
                    <span
                      className={`font-mono-data text-xs ${
                        key === selected ? "text-indigo-200" : "text-zinc-400"
                      }`}
                    >
                      {d}
                    </span>
                    <span className="mt-auto flex w-full flex-col gap-1">
                      {list.slice(0, 3).map((e) => (
                        <span
                          key={`${e.job_id}-${e.kind}-${e.stop_id || ""}`}
                          className="flex items-center gap-1.5"
                        >
                          <span
                            className="h-1.5 w-1.5 shrink-0 rounded-full"
                            style={{
                              backgroundColor:
                                e.color || DOT_HEX[e.kind] || "#71717a",
                            }}
                          />
                          <span className="truncate text-[10px] text-zinc-500">
                            {e.time || e.kind}
                          </span>
                        </span>
                      ))}
                      {list.length > 3 && (
                        <span className="font-mono-data text-[10px] text-zinc-600">
                          +{list.length - 3} more
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap gap-4 px-1 text-xs text-zinc-500">
              {Object.entries(KIND_DOT).map(([k, dot]) => (
                <span key={k} className="inline-flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${dot}`} /> {k}
                </span>
              ))}
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Scheduled on {selected}
              </div>
              <div className="flex items-center gap-2">
                {doneCount > 0 && (
                  <div
                    className="inline-flex rounded-full border border-zinc-800 bg-zinc-950 p-0.5"
                    data-testid="schedule-done-tabs"
                    role="tablist"
                  >
                    {[
                      ["all", `All`],
                      ["active", `Hide done`],
                    ].map(([v, l]) => {
                      const active = (v === "active") === hideDone;
                      return (
                        <button
                          key={v}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          data-testid={`schedule-done-tab-${v}`}
                          onClick={() => setHideDone(v === "active")}
                          className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 ${
                            active
                              ? "bg-zinc-200 text-zinc-900"
                              : "text-zinc-400 hover:text-zinc-200"
                          }`}
                        >
                          {l}
                        </button>
                      );
                    })}
                  </div>
                )}
                <span className="font-mono-data rounded-full border border-zinc-800 bg-zinc-900 px-2.5 py-1 text-[10px] uppercase tracking-widest text-zinc-400">
                  {visibleDay.length} stop{visibleDay.length === 1 ? "" : "s"}
                </span>
              </div>
            </div>
            <div className="mt-3 space-y-3" data-testid="schedule-day-list">
              {visibleDay.length === 0 && (
                <p className="rounded-lg border border-dashed border-zinc-800 p-6 text-center text-sm text-zinc-500">
                  {hideDone && doneCount > 0 ? "All stops done." : "Nothing scheduled."}
                </p>
              )}
              {visibleDay.map((e, i) => {
                const Icon = KIND_ICON[e.kind] || Ruler;
                return (
                  <Link
                    key={`${e.job_id}-${e.kind}-${e.stop_id || ""}`}
                    to={
                      e.entity === "service"
                        ? `/projects/service/${e.job_id}/edit`
                        : `/projects/${e.job_id}?tab=field`
                    }
                    data-testid={`schedule-event-${e.job_id}-${e.kind}${e.stop_id ? "-" + e.stop_id : ""}`}
                    className="group relative flex overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900 transition-all duration-200 hover:-translate-y-0.5 hover:border-zinc-700"
                    style={{ animationDelay: `${i * 40}ms` }}
                  >
                    <span
                      className="w-1 shrink-0"
                      style={{
                        backgroundColor: e.color || DOT_HEX[e.kind] || "#71717a",
                      }}
                    />
                    <span className="min-w-0 flex-1 p-4">
                      <span className="flex items-center justify-between gap-3">
                        <span className="flex min-w-0 items-center gap-1.5">
                          <span
                            className="inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-widest"
                            style={{
                              borderColor: `${e.color || DOT_HEX[e.kind] || "#71717a"}55`,
                              color: e.color || DOT_HEX[e.kind] || "#a1a1aa",
                              backgroundColor: `${e.color || DOT_HEX[e.kind] || "#71717a"}1a`,
                            }}
                          >
                            <Icon size={12} weight="bold" /> {e.kind}
                          </span>
                          <span
                            data-testid={`schedule-status-${e.job_id || e.stop_id || i}-${e.kind}`}
                            className={`inline-flex items-center rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-widest ${
                              STATUS_STYLE[statusLabel(e)] || STATUS_STYLE.Scheduled
                            }`}
                          >
                            {statusLabel(e)}
                          </span>
                        </span>
                        <span className="font-mono-data inline-flex shrink-0 items-center gap-1.5 text-xs text-zinc-400">
                          <Clock size={12} weight="bold" /> {e.time || e.window}
                        </span>
                      </span>

                      <span className="mt-3 block truncate text-lg font-bold leading-tight">
                        {e.project_name}
                      </span>
                      <span className="font-mono-data block text-[11px] uppercase tracking-widest text-yellow-500">
                        {e.code}
                        {e.label ? ` · ${e.label}` : ""}
                      </span>
                      {e.areas?.length > 0 && (
                        <span
                          className="mt-1 block text-xs font-semibold uppercase tracking-widest text-indigo-300"
                          data-testid={`schedule-areas-${e.job_id}-${e.kind}`}
                        >
                          {e.kind} · {e.areas.join(", ")}
                        </span>
                      )}
                      <span className="mt-1 block truncate text-sm text-zinc-300">
                        {e.customer_name}
                      </span>

                      {e.address && (
                        <span className="mt-2 flex items-start gap-1.5 text-sm text-zinc-400">
                          <MapPin
                            size={13}
                            weight="bold"
                            className="mt-0.5 shrink-0"
                          />
                          <span className="min-w-0 flex-1">{e.address}</span>
                        </span>
                      )}

                      <span className="mt-3 flex flex-wrap items-center gap-3 border-t border-zinc-800 pt-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-zinc-950 px-2.5 py-1 text-[11px] text-zinc-300">
                          <User
                            size={12}
                            weight="bold"
                            className="text-zinc-500"
                          />
                          {crewLabel(e.crew)}
                        </span>
                        {e.customer_phone && (
                          <a
                            href={`tel:${e.customer_phone}`}
                            data-testid={`schedule-phone-${e.job_id}`}
                            onClick={(ev) => ev.stopPropagation()}
                            className="font-mono-data inline-flex items-center gap-1.5 rounded-full border border-zinc-800 px-2.5 py-1 text-[11px] text-yellow-400 hover:bg-zinc-800"
                          >
                            <Phone size={12} weight="bold" /> {e.customer_phone}
                          </a>
                        )}
                        <ArrowRight
                          size={14}
                          weight="bold"
                          className="ml-auto text-zinc-700 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-zinc-400"
                        />
                      </span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
